import { type Entitlement, type Tag, type Tenant } from 'src/types/azure'

import type moment from 'moment'

import useUserData from '../services/provide/queries/useUserData'

export type GraphView = {
  name: string
  chartConfig: {
    type: string
    stack: string
    cumulative: boolean
    hideForecast: boolean
    hideBudget: boolean
  }
  selectedTags: Tag[]
  selectedTenants: Tenant[]
  selectedSubscriptions: Entitlement[]
  dateRange: number[]
  graphFilter: string[]
}

interface UseSavedGraphViewsConfig extends Omit<GraphView, 'dateRange'> {
  dateRange: [moment.Moment, moment.Moment]
}

const useSavedGraphViews = (config: UseSavedGraphViewsConfig) => {
  const { data: savedViews, isLoading: isLoadingSavedViews, update: updateUserData } = useUserData<GraphView[]>('savedViews')

  const handleGraphViewSave = async () => {
    if (!config.name) return

    const isNew = !savedViews?.some((view) => view.name === config.name)
    const newGraphView = {
      ...config,
      dateRange: [config.dateRange[0].valueOf(), config.dateRange[1].valueOf()]
    }

    if (isNew) {
      await updateUserData([...(savedViews ?? []), newGraphView])
    } else {
      const updatedGraphViews = savedViews?.map((view) => (view.name === config.name ? newGraphView : view))
      await updateUserData(updatedGraphViews ?? [newGraphView])
    }
  }

  const handleGraphViewRemove = (name: string) => {
    const updatedGraphViews = savedViews?.filter((view) => view.name !== name)
    updateUserData(updatedGraphViews ?? [])
  }

  return {
    savedViews,
    saveCurrentView: handleGraphViewSave,
    removeView: handleGraphViewRemove,
    isLoading: isLoadingSavedViews
  }
}

export default useSavedGraphViews
